// ===========================================
// ADMIN LOG STORE
// ===========================================
// Unified logging for admin panel - tracks animation failures, batch timeouts,
// and other diagnostic events. Groups similar entries to prevent log flooding.

import { create } from 'zustand';

// ===========================================
// LOG ENTRY TYPES
// ===========================================

export type LogCategory = 'animation' | 'batch' | 'network' | 'error' | 'event';
export type LogSeverity = 'warning' | 'error' | 'critical';

// Base interface for all log entries
interface BaseLogEntry {
  // Grouping key (auto-generated from entry details)
  key: string;
  // Category for filtering
  category: LogCategory;
  // Severity level
  severity: LogSeverity;
  // Source location
  fileName: string;
  functionName: string;
  // Human-readable description
  description: string;
  // Additional details (varies by type)
  details: Record<string, unknown>;
  // Occurrence tracking
  count: number;
  firstOccurrence: number;
  lastOccurrence: number;
}

// Animation failure log entry
export interface AnimationLogEntry extends BaseLogEntry {
  category: 'animation';
  details: {
    reason: 'timeout' | 'destroyed' | 'error';
    errorMessage?: string;
    animationType: string;
    targetDescription: string;
    expectedDuration: number;
  };
}

// Batch timeout log entry
export interface BatchLogEntry extends BaseLogEntry {
  category: 'batch';
  details: {
    reason: 'timeout' | 'error';
    errorMessage?: string;
    eventCount: number;
    eventTypes: string[];
    timeoutDuration: number;
    processingStartTime: number;
    lastProcessedEvent?: string;
    lastProcessedIndex?: number;
  };
}

// Network issue log entry
export interface NetworkLogEntry extends BaseLogEntry {
  category: 'network';
  details: {
    reason: 'disconnect' | 'reconnect_failed' | 'resync_failed';
    errorMessage?: string;
    attemptCount?: number;
  };
}

// Generic error log entry
export interface ErrorLogEntry extends BaseLogEntry {
  category: 'error';
  details: {
    errorMessage: string;
    stack?: string;
    context?: Record<string, unknown>;
  };
}

// Event skip log entry (ClientTableMaster filtering)
export interface EventLogEntry extends BaseLogEntry {
  category: 'event';
  details: {
    reason: 'stale' | 'duplicate' | 'future_round';
    eventType: string;
    eventSeq: number;
    lastProcessedSeq: number;
    eventRound: number;
    activeRound: number;
  };
}

// Union type for all log entries
export type AdminLogEntry = AnimationLogEntry | BatchLogEntry | NetworkLogEntry | ErrorLogEntry | EventLogEntry;

// Input types (without auto-generated fields)
export type AdminLogInput = Omit<AdminLogEntry, 'key' | 'count' | 'firstOccurrence' | 'lastOccurrence'>;

// ===========================================
// STORE INTERFACE
// ===========================================

interface AdminLogState {
  logs: Map<string, AdminLogEntry>;
  totalCount: number;

  // Record a log entry (will group by key)
  recordLog: (entry: AdminLogInput) => void;

  // Get all logs as array (sorted by count descending)
  getLogs: (category?: LogCategory) => AdminLogEntry[];

  // Get counts by category
  getCategoryCounts: () => Record<LogCategory, number>;

  // Clear all logs
  clearLogs: () => void;

  // Clear logs by category
  clearCategory: (category: LogCategory) => void;

  // Delete a single log entry by key
  deleteLog: (key: string) => void;
}

// ===========================================
// KEY GENERATION
// ===========================================

function generateKey(entry: AdminLogInput): string {
  // Base key from location and category
  const baseKey = `${entry.category}:${entry.fileName}:${entry.functionName}`;

  // Add category-specific details using type assertions since TypeScript
  // doesn't automatically narrow the details type based on category
  switch (entry.category) {
    case 'animation': {
      const details = entry.details as AnimationLogEntry['details'];
      return `${baseKey}:${details.animationType}:${details.reason}`;
    }
    case 'batch': {
      const details = entry.details as BatchLogEntry['details'];
      return `${baseKey}:${details.reason}:${details.eventCount}`;
    }
    case 'network': {
      const details = entry.details as NetworkLogEntry['details'];
      return `${baseKey}:${details.reason}`;
    }
    case 'error': {
      const details = entry.details as ErrorLogEntry['details'];
      return `${baseKey}:${details.errorMessage?.slice(0, 50)}`;
    }
    case 'event': {
      const details = entry.details as EventLogEntry['details'];
      return `${baseKey}:${details.eventType}:${details.reason}`;
    }
    default:
      return baseKey;
  }
}

// ===========================================
// STORE IMPLEMENTATION
// ===========================================

export const useAdminLogStore = create<AdminLogState>((set, get) => ({
  logs: new Map(),
  totalCount: 0,

  recordLog: (entry) => {
    const key = generateKey(entry);
    const now = Date.now();

    set((state) => {
      const newLogs = new Map(state.logs);
      const existing = newLogs.get(key);

      if (existing) {
        // Update existing entry
        newLogs.set(key, {
          ...existing,
          count: existing.count + 1,
          lastOccurrence: now,
          // Update details with most recent info
          details: { ...existing.details, ...entry.details },
        } as AdminLogEntry);
      } else {
        // Create new entry
        newLogs.set(key, {
          ...entry,
          key,
          count: 1,
          firstOccurrence: now,
          lastOccurrence: now,
        } as AdminLogEntry);
      }

      // Console log with appropriate level
      const prefix = `[Admin Log - ${entry.category.toUpperCase()}]`;
      const message = `${entry.description} in ${entry.functionName} (${entry.fileName})`;

      switch (entry.severity) {
        case 'critical':
          console.error(`${prefix} CRITICAL: ${message}`, entry.details);
          break;
        case 'error':
          console.error(`${prefix} ${message}`, entry.details);
          break;
        case 'warning':
          console.warn(`${prefix} ${message}`, entry.details);
          break;
      }

      return {
        logs: newLogs,
        totalCount: state.totalCount + 1,
      };
    });
  },

  getLogs: (category) => {
    const { logs } = get();
    let entries = Array.from(logs.values());

    if (category) {
      entries = entries.filter(e => e.category === category);
    }

    // Sort by count descending (most frequent issues first)
    return entries.sort((a, b) => b.count - a.count);
  },

  getCategoryCounts: () => {
    const { logs } = get();
    const counts: Record<LogCategory, number> = {
      animation: 0,
      batch: 0,
      network: 0,
      error: 0,
      event: 0,
    };

    logs.forEach((entry) => {
      counts[entry.category] += entry.count;
    });

    return counts;
  },

  clearLogs: () => {
    set({ logs: new Map(), totalCount: 0 });
  },

  clearCategory: (category) => {
    set((state) => {
      const newLogs = new Map(state.logs);
      let removedCount = 0;

      newLogs.forEach((entry, key) => {
        if (entry.category === category) {
          removedCount += entry.count;
          newLogs.delete(key);
        }
      });

      return {
        logs: newLogs,
        totalCount: state.totalCount - removedCount,
      };
    });
  },

  deleteLog: (key) => {
    set((state) => {
      const newLogs = new Map(state.logs);
      const entry = newLogs.get(key);

      if (entry) {
        newLogs.delete(key);
        return {
          logs: newLogs,
          totalCount: state.totalCount - entry.count,
        };
      }

      return state;
    });
  },
}));

// ===========================================
// CONVENIENCE FUNCTIONS
// ===========================================

// Record animation failure (backwards compatible with old API)
export function recordAnimationFailure(failure: {
  fileName: string;
  functionName: string;
  reason: 'timeout' | 'destroyed' | 'error';
  errorMessage?: string;
  animationType: string;
  targetDescription: string;
  expectedDuration: number;
}): void {
  // Skip logging 'destroyed' - it's expected during fullVisualResync and tab switch recovery
  // The animations are correctly detecting destroyed targets and cleaning up gracefully
  if (failure.reason === 'destroyed') {
    return;
  }

  useAdminLogStore.getState().recordLog({
    category: 'animation',
    severity: failure.reason === 'error' ? 'error' : 'warning',
    fileName: failure.fileName,
    functionName: failure.functionName,
    description: `${failure.reason.toUpperCase()} - ${failure.animationType}`,
    details: {
      reason: failure.reason,
      errorMessage: failure.errorMessage,
      animationType: failure.animationType,
      targetDescription: failure.targetDescription,
      expectedDuration: failure.expectedDuration,
    },
  });
}

// Record batch timeout
export function recordBatchTimeout(info: {
  fileName: string;
  functionName: string;
  eventCount: number;
  eventTypes: string[];
  timeoutDuration: number;
  processingStartTime: number;
  lastProcessedEvent?: string;
  lastProcessedIndex?: number;
  errorMessage?: string;
}): void {
  useAdminLogStore.getState().recordLog({
    category: 'batch',
    severity: 'critical',
    fileName: info.fileName,
    functionName: info.functionName,
    description: `BATCH TIMEOUT after ${(info.timeoutDuration / 1000).toFixed(1)}s`,
    details: {
      reason: 'timeout',
      errorMessage: info.errorMessage,
      eventCount: info.eventCount,
      eventTypes: info.eventTypes,
      timeoutDuration: info.timeoutDuration,
      processingStartTime: info.processingStartTime,
      lastProcessedEvent: info.lastProcessedEvent,
      lastProcessedIndex: info.lastProcessedIndex,
    },
  });
}

// Record batch error
export function recordBatchError(info: {
  fileName: string;
  functionName: string;
  eventCount: number;
  eventTypes: string[];
  errorMessage: string;
  processingStartTime: number;
}): void {
  useAdminLogStore.getState().recordLog({
    category: 'batch',
    severity: 'error',
    fileName: info.fileName,
    functionName: info.functionName,
    description: `BATCH ERROR`,
    details: {
      reason: 'error',
      errorMessage: info.errorMessage,
      eventCount: info.eventCount,
      eventTypes: info.eventTypes,
      timeoutDuration: 0,
      processingStartTime: info.processingStartTime,
    },
  });
}

// Record generic error
export function recordAdminError(info: {
  fileName: string;
  functionName: string;
  errorMessage: string;
  stack?: string;
  context?: Record<string, unknown>;
}): void {
  useAdminLogStore.getState().recordLog({
    category: 'error',
    severity: 'error',
    fileName: info.fileName,
    functionName: info.functionName,
    description: info.errorMessage.slice(0, 100),
    details: {
      errorMessage: info.errorMessage,
      stack: info.stack,
      context: info.context,
    },
  });
}

// Record skipped event (from ClientTableMaster filtering)
export function recordSkippedEvent(info: {
  reason: 'stale' | 'duplicate' | 'future_round';
  eventType: string;
  eventSeq: number;
  lastProcessedSeq: number;
  eventRound: number;
  activeRound: number;
}): void {
  useAdminLogStore.getState().recordLog({
    category: 'event',
    severity: 'warning',
    fileName: 'ClientTableMaster.ts',
    functionName: 'isStaleEvent',
    description: `SKIPPED ${info.eventType} (${info.reason})`,
    details: {
      reason: info.reason,
      eventType: info.eventType,
      eventSeq: info.eventSeq,
      lastProcessedSeq: info.lastProcessedSeq,
      eventRound: info.eventRound,
      activeRound: info.activeRound,
    },
  });
}

// ===========================================
// LEGACY EXPORT (backwards compatibility)
// ===========================================
// Re-export for files still using old import
export { useAdminLogStore as useAnimationFailureStore };
export type { AnimationLogEntry as AnimationFailure };
